import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { SimpleAlert } from '../../../../../rbts/helpers';
import { SubSeccionModel } from '../models/SubSeccionModel';
import { generarAsientos } from '../../../../service/boletera/EventosBoletera';

type Props = {
  show: boolean;
  subseccion: SubSeccionModel | null;
  handleClose: (refresh?: boolean) => void;
};

const ModalGenerarAsientos: React.FC<Props> = ({ show, subseccion, handleClose }) => {
  const [numFilas, setNumFilas] = useState('');
  const [porFila, setPorFila] = useState('');
  const [filaInicio, setFilaInicio] = useState('1');
  const [saving, setSaving] = useState(false);

  const total = (Number(numFilas) || 0) * (Number(porFila) || 0);
  const hayExistentes = subseccion && (subseccion.asiento_count || 0) > 0;

  useEffect(() => {
    if (show && subseccion) {
      setNumFilas(subseccion.num_filas ? String(subseccion.num_filas) : '');
      setPorFila(subseccion.asientos_por_fila ? String(subseccion.asientos_por_fila) : '');
      setFilaInicio('1');
    }
  }, [show, subseccion]);

  const handleGenerar = async () => {
    if (!numFilas || Number(numFilas) <= 0) { SimpleAlert('Ingresa el número de filas.', 3000, 'error'); return; }
    if (!porFila || Number(porFila) <= 0) { SimpleAlert('Ingresa los asientos por fila.', 3000, 'error'); return; }
    setSaving(true);
    try {
      await generarAsientos(subseccion!.id, {
        num_filas: Number(numFilas),
        asientos_por_fila: Number(porFila),
        fila_inicio: filaInicio || '1',
      });
      SimpleAlert(`${total.toLocaleString()} asientos generados correctamente.`, 2500, 'success');
      handleClose(true);
    } catch (error: any) {
      SimpleAlert(error?.response?.data?.doc?.message || 'Error al generar asientos.', 3000, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={() => handleClose()} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold">
          Generar asientos — <span className="text-muted fw-normal">{subseccion?.nombre}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="py-6 px-8">
        {hayExistentes && (
          <div className="alert alert-warning py-3 mb-5">
            Esta acción <strong>reemplazará</strong> los {(subseccion!.asiento_count || 0).toLocaleString()} asientos existentes.
          </div>
        )}
        <div className="row g-4">
          <div className="col-6">
            <label className="required fw-bold fs-6 mb-2">Núm. de filas</label>
            <input
              className="form-control"
              type="number"
              min={1}
              placeholder="20"
              value={numFilas}
              onChange={(e) => setNumFilas(e.target.value)}
            />
          </div>
          <div className="col-6">
            <label className="required fw-bold fs-6 mb-2">Asientos por fila</label>
            <input
              className="form-control"
              type="number"
              min={1}
              placeholder="30"
              value={porFila}
              onChange={(e) => setPorFila(e.target.value)}
            />
          </div>
          <div className="col-6">
            <label className="fw-bold fs-6 mb-2">Fila inicial</label>
            <input
              className="form-control"
              placeholder="1 ó A"
              value={filaInicio}
              onChange={(e) => setFilaInicio(e.target.value)}
            />
            <div className="text-muted fs-7 mt-1">Número o letra (ej. 1, A)</div>
          </div>
        </div>
        {total > 0 && (
          <div className="text-muted fs-7 mt-5">
            Se generarán <strong>{total.toLocaleString()}</strong> asientos ({numFilas} filas × {porFila} por fila).
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-light me-3" onClick={() => handleClose()}>Cancelar</button>
        <button className="btn btn-primary" onClick={handleGenerar} disabled={saving || total === 0}>
          {saving ? <><span className="spinner-border spinner-border-sm me-2" />Generando…</> : 'Generar asientos'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export { ModalGenerarAsientos };
